import ComputerIcon from '@mui/icons-material/Computer';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { registerLocaleResources, useTranslation } from '@/modules/i18n/useTranslation';
import { type ThemePreference, useThemePreference } from '@/modules/theme';

import en from './locales/en.json';
import es from './locales/es.json';
import ru from './locales/ru.json';
import { styles } from './style';

registerLocaleResources('ThemeSwitch', { en, es, ru });

declare module '@/modules/i18n/namespaces' {
    interface LocaleNamespaces {
        ThemeSwitch: typeof en;
    }
}

const OPTIONS = [
    { value: 'light', Icon: LightModeIcon },
    { value: 'dark', Icon: DarkModeIcon },
    { value: 'system', Icon: ComputerIcon },
] as const satisfies readonly { value: ThemePreference; Icon: typeof LightModeIcon }[];

const ThemeSwitch = () => {
    const { t } = useTranslation('ThemeSwitch');
    const { preference, setPreference } = useThemePreference();

    return (
        <ToggleButtonGroup
            exclusive
            size="small"
            value={preference}
            aria-label={t('label')}
            // `null` when the pressed button is the active one: a preference stays selected, never cleared to nothing
            onChange={(_event, next: ThemePreference | null) => next && setPreference(next)}
        >
            {OPTIONS.map(({ value, Icon }) => (
                <ToggleButton key={value} value={value} title={t(value)} aria-label={t(value)}>
                    <Icon sx={styles.icon}/>
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    );
};

export default ThemeSwitch;
